import { SLICE_REGISTRY } from './constants';

/**
 * Extracts and returns a Set of valid product IDs from an array of sellable products.
 *
 * @param {Array<Object>} sellableProducts - Array of product objects, each containing an id property
 * @returns {Set<string|number>} A Set containing all valid product IDs
 */
export const getValidIds = (sellableProducts) => new Set([...(sellableProducts?.map((product) => product.id) || [])]);

/**
 * Processes a slice configuration to remove invalid product references and returns the updated slice
 * along with the count of invalid products that were removed.
 *
 * @param {Object} slice - The slice configuration object
 * @param {Set<string|number>} validIds - Set of valid product IDs to check against
 * @returns {Object} Process result object
 * @returns {Object} result.slice - The processed slice with invalid products removed
 * @returns {number} result.invalidCount - Number of invalid products that were removed
 */
export const processInvalidProducts = (slice, validIds) => {
    const SliceComponent = SLICE_REGISTRY[slice.displayType];
    const productListKey = SliceComponent?.productListKey;
    const productList = slice[productListKey];

    if (!productList) return { slice, invalidCount: 0 };

    const validItems = productList.filter((item) => validIds.has(item.id));
    const invalidCount = productList.length - validItems.length;

    return {
        slice: {
            ...slice,
            [productListKey]: validItems,
        },
        invalidCount,
    };
};

/**
 * Calculates the total number of invalid product references in a storefront configuration.
 *
 * @param {Object} config - The storefront configuration object containing slices
 * @param {Array<Object>} sellableProducts - Array of valid sellable products
 * @returns {number} Total count of invalid product references across all slices
 */
export const getInvalidProductCount = (config, sellableProducts) => {
    const validIds = getValidIds(sellableProducts);
    return config?.slices?.reduce((total, slice) => total + processInvalidProducts(slice, validIds).invalidCount, 0);
};
